import { queryOptions } from '@tanstack/react-query';
import {
    Level,
    LevelCustomCopy,
    LevelCustomCopyStatus as LevelCustomCopyStatus,
    LevelCustomCopyType as LevelCustomCopyType,
    LevelNote,
    LevelNoteType,
    LevelUpdate,
    LevelUpdateType,
    ResolvedLevel,
} from '../../types/levels';
import { PaginatedQuery, PaginatedResponse } from '../../types/api';
import { ApiError } from '~/components/Providers/APIClient';
import { HistoryEntry } from '../../types/changelog';
import { LevelPack } from '../../types/packs';
import { BaseUser } from '../../types/users';
import { apiQueryFn } from '../utils';
import { LevelResolvedRecord } from '~/util/types/submissions';

export type levelsQueryParams = {
    list: string;
    exclude_legacy?: boolean;
    exclude_pending?: boolean;
    exclude_removed?: boolean;
    at?: string;
    authed?: boolean;
};

export const levelsQuery = ({
    list,
    exclude_legacy,
    exclude_pending,
    exclude_removed,
    at,
    authed,
}: levelsQueryParams) =>
    queryOptions<Level[], ApiError>({
        queryKey: [
            'list',
            list,
            exclude_legacy,
            exclude_pending,
            exclude_removed,
            at,
            authed,
        ],
        queryFn: apiQueryFn<Level[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels`,
            query: {
                exclude_legacy: exclude_legacy ?? false,
                exclude_pending: exclude_pending ?? false,
                exclude_removed: exclude_removed ?? false,
                at,
            },
            attachAuth: true,
        }),
    });

export type levelQueryParams = {
    list: string;
    levelid: string;
};
export const levelQuery = ({ list, levelid }: levelQueryParams) =>
    queryOptions<ResolvedLevel, ApiError>({
        queryKey: ['list', list, levelid, 'base'],
        queryFn: apiQueryFn<ResolvedLevel>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}`,
        }),
    });

export const levelCreatorsQuery = ({ list, levelid }: levelQueryParams) =>
    queryOptions<BaseUser[], ApiError>({
        queryKey: ['list', list, levelid, 'creators'],
        queryFn: apiQueryFn<BaseUser[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/creators`,
        }),
    });

export const levelRecordsQuery = ({
    list,
    levelid,
    submitter_filter = '',
    ...paginationParams
}: levelQueryParams & PaginatedQuery & { submitter_filter?: string }) =>
    queryOptions<PaginatedResponse<LevelResolvedRecord>, ApiError>({
        queryKey: [
            'list',
            list,
            levelid,
            'records',
            submitter_filter,
            paginationParams,
        ],
        queryFn: apiQueryFn<PaginatedResponse<LevelResolvedRecord>>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/records`,
            query: {
                submitter_filter: `%${submitter_filter}%`,
                ...paginationParams,
            },
        }),
    });

export const levelPacksQuery = ({ list, levelid }: levelQueryParams) =>
    queryOptions<LevelPack[], ApiError>({
        queryKey: ['list', list, levelid, 'packs'],
        queryFn: apiQueryFn<LevelPack[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/packs`,
        }),
    });

export const levelHistoryQuery = ({ list, levelid }: levelQueryParams) =>
    queryOptions<HistoryEntry[], ApiError>({
        queryKey: ['list', list, levelid, 'history'],
        queryFn: apiQueryFn<HistoryEntry[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/history`,
        }),
    });

export type levelCustomCopiesQueryParams = levelQueryParams & {
    status?: LevelCustomCopyStatus;
    type?: LevelCustomCopyType;
};

export const levelCustomCopiesQuery = ({
    list,
    levelid,
    status,
    type,
}: levelCustomCopiesQueryParams) =>
    queryOptions<LevelCustomCopy[], ApiError>({
        queryKey: ['list', list, levelid, 'level_custom_copies', status, type],
        queryFn: apiQueryFn<LevelCustomCopy[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/custom-copies`,
            query: {
                ...(status ? { status_filter: status } : {}),
                ...(type ? { type_filter: type } : {}),
            },
        }),
    });

export type levelNotesQueryParams = levelQueryParams & {
    type?: LevelNoteType;
};

export const levelNotesQuery = ({
    list,
    levelid,
    type,
}: levelNotesQueryParams) =>
    queryOptions<LevelNote[], ApiError>({
        queryKey: ['list', list, levelid, 'level_notes', type],
        queryFn: apiQueryFn<LevelNote[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/notes`,
            query: {
                ...(type ? { type_filter: type } : {}),
            },
            attachAuth: true,
        }),
    });

export type levelUpdatesQueryParams = levelQueryParams & {
    type?: LevelUpdateType;
};

export const levelUpdatesQuery = ({
    list,
    levelid,
    type,
}: levelUpdatesQueryParams) =>
    queryOptions<LevelUpdate[], ApiError>({
        queryKey: ['list', list, levelid, 'level_updates', type],
        queryFn: apiQueryFn<LevelUpdate[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/updates`,
            query: {
                ...(type ? { type_filter: type } : {}),
            },
        }),
    });
